import React, { useState, useEffect } from 'react';
import { Container, Button, Table, Modal, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { userService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PERMISSION_DETAILS = {
  power: { label: 'Power Control', desc: 'Wake, Shutdown, Restart' },
  remote_view: { label: 'Live Mirror', desc: 'Live screen mirroring' },
  kiosk: { label: 'Kiosk Control', desc: 'Start/stop kiosk URL settings' },
  manage_assets: { label: 'Edit Assets', desc: 'Create, modify, delete hosts & clusters' }
};

function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });

  // Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Form States
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getAll();
      setUsers(data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch user list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const triggerAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 5000);
  };

  const handlePermissionToggle = (perm) => {
    setSelectedPermissions(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  const openAddModal = () => {
    setUsername('');
    setPassword('');
    setRole('user');
    setSelectedPermissions(['power', 'remote_view']); // default permissions
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      triggerAlert('Username and password are required', 'danger');
      return;
    }

    try {
      setModalLoading(true);
      const payload = {
        username,
        password,
        role,
        permissions: role === 'admin' ? [] : selectedPermissions
      };
      await userService.create(payload);
      triggerAlert('User account created successfully');
      setShowAddModal(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
      triggerAlert(err.response?.data?.error || 'Failed to create user', 'danger');
    } finally {
      setModalLoading(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setUsername(user.username);
    setPassword(''); // leave blank for no change
    setRole(user.role);
    setSelectedPermissions(user.permissions || []);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      triggerAlert('Username cannot be empty', 'danger');
      return;
    }

    try {
      setModalLoading(true);
      const payload = {
        username,
        role,
        permissions: role === 'admin' ? [] : selectedPermissions
      };
      if (password) {
        payload.password = password;
      }
      await userService.update(selectedUser.id, payload);
      triggerAlert('User account updated successfully');
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
      triggerAlert(err.response?.data?.error || 'Failed to update user', 'danger');
    } finally {
      setModalLoading(false);
    }
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setModalLoading(true);
      await userService.delete(selectedUser.id);
      triggerAlert('User account deleted successfully');
      setShowDeleteModal(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
      triggerAlert(err.response?.data?.error || 'Failed to delete user', 'danger');
    } finally {
      setModalLoading(false);
    }
  };

  const formatLastLogin = (dateStr) => {
    if (!dateStr) return 'Never logged in';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <div className="py-4" style={{ minHeight: '100vh', paddingLeft: '2rem', paddingRight: '2rem' }}>
      <Container fluid className="px-0">
        
        {/* Header Block */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="mb-1 text-theme-main" style={{ fontSize: '1.75rem', fontWeight: 700 }}>
              <i className="fas fa-users-cog me-2 text-primary"></i>
              User Accounts
            </h1>
            <p className="text-theme-muted mb-0" style={{ fontSize: '0.85rem' }}>
              Assign role-based access rights and policies to LANtern operators.
            </p>
          </div>
          <Button className="btn-premium-primary" onClick={openAddModal}>
            <i className="fas fa-user-plus me-2"></i>
            Create New Account
          </Button>
        </div>

        {/* Global Alert Notification */}
        {alert.show && (
          <Alert variant={alert.variant} className="border-0 mb-4 shadow-sm" onClose={() => setAlert({ ...alert, show: false })} dismissible>
            {alert.message}
          </Alert>
        )}

        {/* Content Box */}
        {loading ? (
          <div className="glass-card text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted mb-0">Fetching user accounts...</p>
          </div>
        ) : error ? (
          <div className="glass-card text-center py-5">
            <Alert variant="danger" className="border-0 d-inline-block">
              {error}
            </Alert>
            <br />
            <Button className="btn-premium-primary mt-2" onClick={fetchUsers}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="glass-card">
            <div className="custom-table-container">
              <Table responsive className="table-custom mb-0">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Permissions Policy</th>
                    <th>Last Active Connection</th>
                    <th style={{ width: '120px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <span className="text-theme-main font-semibold" style={{ fontSize: '0.925rem' }}>
                          {u.username}
                        </span>
                        {u.username === currentUser?.username && (
                          <Badge bg="primary" className="ms-2" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
                            You
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Badge bg={u.role === 'admin' ? 'danger' : 'secondary'} className="text-uppercase" style={{ fontSize: '0.7rem', padding: '0.35rem 0.5rem' }}>
                          {u.role}
                        </Badge>
                      </td>
                      <td>
                        {u.role === 'admin' ? (
                          <span className="text-theme-muted fs-7 italic">Full Administrative Bypass</span>
                        ) : u.permissions && u.permissions.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {u.permissions.map(p => (
                              <Badge key={p} bg="dark" className="border border-secondary text-light fs-8">
                                {PERMISSION_DETAILS[p]?.label || p}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-danger fs-8">No action privileges assigned</span>
                        )}
                      </td>
                      <td className="text-theme-muted" style={{ fontSize: '0.8rem' }}>
                        {formatLastLogin(u.last_login)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="d-flex justify-content-end gap-1.5">
                          <button
                            className="btn-action-premium"
                            onClick={() => openEditModal(u)}
                            title="Edit Permissions"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn-action-premium text-danger"
                            onClick={() => openDeleteModal(u)}
                            disabled={u.username === currentUser?.username}
                            title={u.username === currentUser?.username ? "Cannot delete yourself" : "Delete Account"}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {/* Create Modal */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="md" centered>
          <div className="modal-content-premium">
            <Modal.Header closeButton className="modal-header-premium border-0">
              <Modal.Title style={{ fontWeight: 700 }}>
                <i className="fas fa-user-plus me-2 text-primary"></i>
                Create Account
              </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleAddSubmit}>
              <Modal.Body className="px-4 py-3">
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Username</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    className="form-input-premium w-100"
                    placeholder="Enter unique username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Password</Form.Label>
                  <Form.Control
                    type="password"
                    required
                    className="form-input-premium w-100"
                    placeholder="Enter security password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">System Access Role</Form.Label>
                  <Form.Select
                    className="form-input-premium w-100"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    <option value="user">Standard User (Permission Policy-based)</option>
                    <option value="admin">Administrator (Unrestricted access)</option>
                  </Form.Select>
                </Form.Group>

                {role === 'user' && (
                  <Form.Group className="mb-2">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider d-block mb-2">Granted Capabilities</Form.Label>
                    <div style={{ background: 'rgba(0, 0, 0, 0.1)', padding: '1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
                      {Object.keys(PERMISSION_DETAILS).map((perm) => (
                        <Form.Check
                          key={perm}
                          type="switch"
                          id={`perm-add-${perm}`}
                          className="text-theme-main mb-2.5"
                          label={
                            <div>
                              <strong style={{ fontSize: '0.875rem' }}>{PERMISSION_DETAILS[perm].label}</strong>
                              <span className="text-theme-muted d-block" style={{ fontSize: '0.725rem' }}>{PERMISSION_DETAILS[perm].desc}</span>
                            </div>
                          }
                          checked={selectedPermissions.includes(perm)}
                          onChange={() => handlePermissionToggle(perm)}
                        />
                      ))}
                    </div>
                  </Form.Group>
                )}
              </Modal.Body>
              <Modal.Footer className="modal-footer-premium border-0 gap-2">
                <Button className="btn-premium-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button className="btn-premium-primary" type="submit" disabled={modalLoading}>
                  {modalLoading ? <Spinner size="sm" animation="border" /> : 'Create Account'}
                </Button>
              </Modal.Footer>
            </Form>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="md" centered>
          <div className="modal-content-premium">
            <Modal.Header closeButton className="modal-header-premium border-0">
              <Modal.Title style={{ fontWeight: 700 }}>
                <i className="fas fa-user-edit me-2 text-primary"></i>
                Edit Account Policies
              </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleEditSubmit}>
              <Modal.Body className="px-4 py-3">
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Username</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    className="form-input-premium w-100"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Password (Optional)</Form.Label>
                  <Form.Control
                    type="password"
                    className="form-input-premium w-100"
                    placeholder="Leave blank to keep current password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Access Role</Form.Label>
                  <Form.Select
                    className="form-input-premium w-100"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    <option value="user">Standard User (Permission Policy-based)</option>
                    <option value="admin">Administrator (Unrestricted access)</option>
                  </Form.Select>
                </Form.Group>

                {role === 'user' && (
                  <Form.Group className="mb-2">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider d-block mb-2">Granted Capabilities</Form.Label>
                    <div style={{ background: 'rgba(0, 0, 0, 0.1)', padding: '1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
                      {Object.keys(PERMISSION_DETAILS).map((perm) => (
                        <Form.Check
                          key={perm}
                          type="switch"
                          id={`perm-edit-${perm}`}
                          className="text-theme-main mb-2.5"
                          label={
                            <div>
                              <strong style={{ fontSize: '0.875rem' }}>{PERMISSION_DETAILS[perm].label}</strong>
                              <span className="text-theme-muted d-block" style={{ fontSize: '0.725rem' }}>{PERMISSION_DETAILS[perm].desc}</span>
                            </div>
                          }
                          checked={selectedPermissions.includes(perm)}
                          onChange={() => handlePermissionToggle(perm)}
                        />
                      ))}
                    </div>
                  </Form.Group>
                )}
              </Modal.Body>
              <Modal.Footer className="modal-footer-premium border-0 gap-2">
                <Button className="btn-premium-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button className="btn-premium-primary" type="submit" disabled={modalLoading}>
                  {modalLoading ? <Spinner size="sm" animation="border" /> : 'Save Changes'}
                </Button>
              </Modal.Footer>
            </Form>
          </div>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm">
          <div className="modal-content-premium border border-danger">
            <Modal.Header className="modal-header-premium border-0 pb-0 justify-content-center">
              <Modal.Title style={{ fontWeight: 700, color: '#f43f5e', fontSize: '1.25rem' }}>
                <i className="fas fa-exclamation-triangle me-2"></i>
                Confirm Delete
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center pt-2 pb-3">
              <p className="text-theme-main mb-0" style={{ fontSize: '0.9rem' }}>
                Are you sure you want to permanently delete user <strong>{selectedUser?.username}</strong>?
              </p>
            </Modal.Body>
            <Modal.Footer className="modal-footer-premium border-0 pt-0 pb-3 justify-content-center gap-2">
              <Button className="btn-premium-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button className="btn-danger border-0 px-3 py-1.5 rounded-md font-semibold text-white fs-7" onClick={handleDeleteConfirm} disabled={modalLoading}>
                {modalLoading ? <Spinner size="sm" animation="border" /> : 'Yes, Delete'}
              </Button>
            </Modal.Footer>
          </div>
        </Modal>

      </Container>
    </div>
  );
}

export default UsersPage;
